import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Alert } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { closeSnackbar, setActiveOrganization } from "@/store/uiSlice";
import { getProfile, isLoggedInSelector, setUser } from "@/features/auth/slice";
import { useLazyFetchOrganizationAliasQuery } from "@/features/organizations/api/websiteApi";

type Params = {
  organizationId?: string;
  organizationAlias?: string;
};

const IMPRESIONES_AEM = "njTiw4ZSrCL5dWje47UL";
const BITELINK = "bitelink";
const LA_CAMPANA = "WuTqUgHq1PV5j9hx5z0o";
const MARINA = "KTte3JfRIfsX7RnkortX";

const ORGANIZATION_DOMAIN: Record<string, string> = {
  ["www.impresionesaem.com"]: IMPRESIONES_AEM,
  ["impresionesaem.com"]: IMPRESIONES_AEM,
  ["bitelink.app"]: BITELINK,
  ["www.lacampananic.com"]: LA_CAMPANA,
  ["lacampananic.com"]: LA_CAMPANA,
  ["marinapuestadelsol.com"]: MARINA,
  ["localhost:5173"]: BITELINK,
};

const GlobalRoute = () => {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const snackbar = useAppSelector((state) => state.ui.snackbar);
  const params = useParams<Params>();

  const [fetchAlias] = useLazyFetchOrganizationAliasQuery();

  const getOrganizationId = async () => {
    // check domain
    let id = null;
    const DOMAIN = document.location.host;
    const IS_ROOT = document.location.pathname === "/";
    const isBitelink = DOMAIN.includes("bitelink.app");

    //This is the correct way for avoiding a redirect
    // if (isBitelink) {
    const isDevMode = DOMAIN.includes("localhost");
    if (isBitelink || isDevMode) {
      if (IS_ROOT) {
        window.location.replace("https://bitelink.website");
      }

      // check id in admin
      if (params?.organizationId) {
        id = params.organizationId;
      }

      // check alias
      if (params?.organizationAlias) {
        const alias = await fetchAlias(params.organizationAlias);

        if (alias.data?.organizationId) {
          id = alias.data.organizationId;
        }
      }
    } else {
      id = ORGANIZATION_DOMAIN[DOMAIN];
    }

    return id || BITELINK;
  };

  const checkOrganization = async () => {
    const organizationId = await getOrganizationId();
    if (organizationId) {
      dispatch(setActiveOrganization(organizationId));
    }
  };

  useEffect(() => {
    checkOrganization();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: any) => {
      if (user) {
        dispatch(
          setUser({
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
          })
        );
        dispatch(getProfile(user.uid));
      }
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <>
      <Outlet />
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={snackbar.isOpen}
        onClose={() => dispatch(closeSnackbar())}
        autoHideDuration={4000}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GlobalRoute;
